import React, { useState } from 'react';
import { captureError } from '@/utils/errorHandler';

function formatDateTime(dateString) {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function AdminPrivateNotesSection({ appId, adminNotes, onRefresh, authFetch, showToast }) {
  const [newNoteContent, setNewNoteContent] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState(null);

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) {
      showToast('Please enter a note', 'error');
      return;
    }

    setAddingNote(true);
    try {
      const response = await authFetch(`/api/admin/admin-notes?appId=${appId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: newNoteContent.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add note');
      }

      setNewNoteContent('');
      showToast('✅ Private note added!', 'success');
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      captureError(error, {
        action: 'handleAddNote',
        component: 'AdminPrivateNotesSection',
        extra: { appId },
      });
      console.error('Error adding note:', error);
      showToast(error.message || 'Failed to add note', 'error');
    } finally {
      setAddingNote(false);
    }
  };

  const handleStartEdit = (note) => {
    setEditingNoteId(note.id);
    setEditingContent(note.content);
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditingContent('');
  };

  const handleSaveEdit = async (noteId) => {
    if (!editingContent.trim()) {
      showToast('Please enter a note', 'error');
      return;
    }

    setSaving(true);
    try {
      const response = await authFetch(`/api/admin/admin-notes?appId=${appId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          noteId,
          content: editingContent.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update note');
      }

      setEditingNoteId(null);
      setEditingContent('');
      showToast('✅ Note updated!', 'success');
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      captureError(error, {
        action: 'handleSaveEdit',
        component: 'AdminPrivateNotesSection',
        extra: { appId, noteId },
      });
      console.error('Error updating note:', error);
      showToast(error.message || 'Failed to update note', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!confirm('Are you sure you want to delete this note?')) {
      return;
    }

    setDeletingNoteId(noteId);
    try {
      const response = await authFetch(`/api/admin/admin-notes?appId=${appId}&noteId=${noteId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete note');
      }

      showToast('✅ Note deleted!', 'success');
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      captureError(error, {
        action: 'handleDeleteNote',
        component: 'AdminPrivateNotesSection',
        extra: { appId, noteId },
      });
      console.error('Error deleting note:', error);
      showToast(error.message || 'Failed to delete note', 'error');
    } finally {
      setDeletingNoteId(null);
    }
  };

  return (
    <div className="bg-white rounded-lg border-2 border-purple-200 p-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-lg font-semibold text-gray-900">🔒 Private Admin Notes</h2>
        <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">
          Admin Only
        </span>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Add private notes to help you remember important information when working on this app or moving it to production.
        These notes are only visible to admins.
      </p>
      
      {/* Notes Display */}
      {adminNotes && adminNotes.length > 0 ? (
        <div className="space-y-3 mb-6 pb-6 border-b border-gray-200 max-h-96 overflow-y-auto">
          {adminNotes.map((note) => (
            <div key={note.id} className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              {editingNoteId === note.id ? (
                <div>
                  <textarea
                    value={editingContent}
                    onChange={(e) => setEditingContent(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm mb-3"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSaveEdit(note.id)}
                      disabled={saving}
                      className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={saving}
                      className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-gray-900">
                          {note.authorEmail || 'Unknown'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDateTime(note.createdAt)}
                        </span>
                        {note.updatedAt && note.updatedAt !== note.createdAt && (
                          <span className="text-xs text-gray-400 italic">
                            (edited {formatDateTime(note.updatedAt)})
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.content}</p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleStartEdit(note)}
                        className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        disabled={deletingNoteId === note.id}
                        className="text-xs text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
                      >
                        {deletingNoteId === note.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500 mb-6 pb-6 border-b border-gray-200">
          <p className="text-sm">No private notes yet. Add a note below to get started.</p>
        </div>
      )}

      {/* Add Note Form */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Add a private note:
        </label>
        <textarea
          value={newNoteContent}
          onChange={(e) => setNewNoteContent(e.target.value)}
          rows={4}
          placeholder="e.g., Remember to update environment variables before moving to production..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm mb-3"
        />
        <button
          onClick={handleAddNote}
          disabled={addingNote || !newNoteContent.trim()}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          {addingNote ? 'Adding...' : '➕ Add Private Note'}
        </button>
      </div>
    </div>
  );
}

